import { Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import useUiContext from "@/custolink-hooks/useUiContext";
import { I18nextProvider } from "react-i18next";
import { initI18n } from "@/i18n";
import type { i18n as I18nInstance } from "i18next";
import { setLanguageHeaders } from "@/custolink-logic/api/JsonApi";

export default function AssetFrontendArea() {
  const uic = useUiContext();
  useLookAndFeel();

  const [i18nInst, setI18nInst] = useState<I18nInstance | null>(null);

  useEffect(() => {
    initI18n(
      "asset_frontend",
      uic.organisationContext.assetFrontendi18nLanguageCodes,
    ).then((inst) => {
      if (i18nInst == null) {
        setI18nInst(inst);

        /**
         * "lang = inst.resolvedLanguage || inst.language"
         *   is what the frontend actually will use, since it finds translations for this
         * "inst.language" is what it got out from the Accept-Language header (or storage)
         *
         * In practice:
         * 1. 'lang' will be one of the supported languages for the asset frontend, if the browser presents a supported language. Otherwise, it will be english.
         * 2. inst.language can be another language which is not supported for the asset frontend. But the ADMIN may have added Language Variants in the filemanager which can use special language codes.
         */
        const lang = inst.resolvedLanguage || inst.language;

        uic.setAssetFrontendlanguageCode(lang);
        setLanguageHeaders(lang, inst.language);
      }
    });
  }, []);

  if (!i18nInst) return null; // or a proper loading component

  return (
    <I18nextProvider i18n={i18nInst}>
      <div key={i18nInst.resolvedLanguage || i18nInst.language}>
        <Outlet />
      </div>
    </I18nextProvider>
  );
}

function useLookAndFeel() {
  const uic = useUiContext();
  useEffect(() => {
    const root = document.documentElement;
    const themeColor =
      uic.organisationContext.lookAndFeel.assetFrontendBgcolorHex ?? "#002EFF";
    const theme: Record<string, string> = {
      "--background-color-asset-frontend": themeColor,
    };
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Update or create <meta name="theme-color" />
    const metaTag = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (metaTag) {
      metaTag.setAttribute("content", themeColor);
    }
  }, []);
}
